import React from "react";
import "./GuestBook.css";
import translations from "../../data/translations";
import Modal from "../../components/modal/Modal";
import MessageModal from "../../components/modal/MessageModal";
import GuestBookForm from "../../components/form/Form";
import MessageList from "../../components/message/MessageList";
import DollhouseDecor, {
  DollhouseBackdrop,
  DollhouseVillage,
} from "../../components/decor/DollhouseDecor";

const PAGE_SIZE = 12;
const API_BASE = "https://e03nc8puh0.execute-api.eu-west-3.amazonaws.com/prod";

function GuestBookPage() {
  const [name, setName] = React.useState("");
  const [country, setCountry] = React.useState(null);
  const [city, setCity] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [language, setLanguage] = React.useState("english");
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalMessage, setModalMessage] = React.useState("");
  const [messages, setMessages] = React.useState([]);
  const [messagesLoading, setMessagesLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  const [selectedMessage, setSelectedMessage] = React.useState(null);

  const t = translations[language];
  const isRtl = language === "hebrew" || language === "arabic";

  const fetchMessages = React.useCallback(() => {
    setMessagesLoading(true);
    setFetchError(false);
    fetch(`${API_BASE}/messages`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setMessages([...data].reverse()); // newest first
        setMessagesLoading(false);
      })
      .catch(() => {
        setFetchError(true);
        setMessagesLoading(false);
      });
  }, []);

  React.useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  function validate() {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!country || !country.value || !country.value.trim())
      newErrors.country = "Country is required";
    if (!message.trim()) newErrors.message = "Message is required";
    if (message.length > 1000)
      newErrors.message = "Message must be under 1000 characters";
    return newErrors;
  }

  async function sendMessage(name, country, city, message) {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    // The entry Lambda can time out on a cold start; an immediate retry
    // hits a warm instance and usually succeeds.
    const postEntry = () =>
      fetch(`${API_BASE}/entry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, country, city, message }),
      });
    try {
      let response;
      try {
        response = await postEntry();
      } catch {
        response = null;
      }
      if (!response || response.status !== 200) {
        await new Promise((r) => setTimeout(r, 400));
        response = await postEntry();
      }
      setLoading(false);
      if (response.status === 200) {
        setName("");
        setCountry(null);
        setCity(null);
        setMessage("");
        setModalMessage(t.sent);
        setModalOpen(true);
        fetchMessages();
      } else {
        setModalMessage(t.failed);
        setModalOpen(true);
      }
    } catch (error) {
      setLoading(false);
      setModalMessage(t.failed);
      setModalOpen(true);
    }
  }

  const displayedMessages = React.useMemo(
    () => messages.slice(0, visibleCount),
    [messages, visibleCount]
  );
  const hasMore = visibleCount < messages.length;

  const loadMore = React.useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, messages.length));
  }, [messages.length]);

  return (
    <div className="gb-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="sr-only" aria-live="polite">
        {modalMessage}
      </div>

      <header className="gb-header">
        <img
          src="https://minas-guest-book.s3.eu-west-3.amazonaws.com/images/Dollhouse+logo+grey+3.svg"
          className="gb-logo"
          alt="Dollhouse logo"
        />
        <div className="gb-header-text">
          <h1 className="gb-title">{t.heroTitle}</h1>
          {!messagesLoading && !fetchError && messages.length > 0 && (
            <p className="gb-count">
              {messages.length} {t.notesLabel}
            </p>
          )}
        </div>
        <a className="gb-sign-cta" href="#guestbook-form">
          {t.signCta}
        </a>
      </header>

      <section className="gb-form-section" id="guestbook-form">
        <DollhouseDecor />
        <div className="gb-form-card">
          <div className="gb-form-heading">
            <h2 className="gb-form-title">{t.leaveNote}</h2>
            <img
              src="https://minas-guest-book.s3.eu-west-3.amazonaws.com/images/Dollhouse+logo+grey+3.svg"
              className="gb-form-logo"
              alt=""
              aria-hidden="true"
            />
          </div>
          <p className="gb-dedication">{t.title}</p>
          <GuestBookForm
            name={name}
            setName={setName}
            country={country}
            setCountry={setCountry}
            city={city}
            setCity={setCity}
            message={message}
            setMessage={setMessage}
            language={language}
            setLanguage={setLanguage}
            errors={errors}
            loading={loading}
            sendMessage={sendMessage}
          />
        </div>
      </section>

      <main className="gb-main">
        <DollhouseBackdrop />
        {fetchError ? (
          <div className="wall-state" role="alert">
            <div className="wall-state-icon" aria-hidden="true">
              📪
            </div>
            <h3 className="wall-state-title">{t.loadError}</h3>
            <button
              type="button"
              className="load-more-button"
              onClick={fetchMessages}
            >
              {t.retry}
            </button>
          </div>
        ) : (
          <MessageList
            messages={displayedMessages}
            loading={messagesLoading}
            hasMore={hasMore}
            loadMore={loadMore}
            onMessageClick={setSelectedMessage}
            labels={t}
          />
        )}
      </main>

      <footer className="gb-footer">
        <DollhouseVillage />
        <div className="gb-footer-content">
          <div>
            <p className="gb-footer-title">Thanks for visiting</p>
            <p className="gb-footer-text">
              Every note helps us keep this guest book alive. Come back any
              time to see new entries.
            </p>
          </div>
          <div className="gb-footer-links">
            <a href="#guestbook-form">Sign</a>
            <span aria-hidden="true">·</span>
            <a href="mailto:minaminis@gmail.com">Contact</a>
          </div>
        </div>
      </footer>

      <Modal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        modalMessage={modalMessage}
        language={language}
      />

      {selectedMessage && (
        <MessageModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
        />
      )}
    </div>
  );
}

export default GuestBookPage;
